import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Disclosure } from '@headlessui/react';
import {
  TrashIcon,
  PlusIcon,
  ServerStackIcon,
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
  ChevronRightIcon,
  CpuChipIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { useForm } from '../../hooks/useForm';
import { FormField, FormTextArea, SmartFormActions } from '../shared/form';
import { SchemaBuilder } from '../schema-builder';
import { useModelConfigs } from '../../hooks/useModelConfigs';
import { ModelConfigPickerOptions, ModelLifecycleNote, ModelPolicyNote } from '../modelconfigs/ModelConfigPickerOptions';
import { FailoverModelConfigPicker } from '../modelconfigs/FailoverModelConfigPicker';
import { useAccountModelPicks } from '../../hooks/useAccountModels';
import { usePrompts } from '../../hooks/usePrompts';
import {
  useAgentMcpConnections,
  useRemoveAgentMcpConnection,
  useActiveAgentPrompt,
} from '../../hooks/useAgents';
import { AgentMcpConnectionSelector } from './AgentMcpConnectionSelector';
import { AgentPromptSelector } from './AgentPromptSelector';
import { Agent } from '../../types';
import { useFlag } from '../../hooks/useFeatureFlags';

export interface AgentFormData {
  name: string;
  description: string;
  metadata: string;
  isActive: boolean;
  modelConfigId: string;
  /** '' = "No failover" (task 1301). */
  failoverModelConfigId: string;
  tools: string[];
  selectedPromptId?: string;
}

// Available tools that agents can use during collaboration execution
const AVAILABLE_TOOLS = [
  { value: 'file.read', label: 'File Read', description: 'Read files from data directory' },
  { value: 'file.write', label: 'File Write', description: 'Write files to data directory' },
  { value: 'http.fetch', label: 'HTTP Fetch', description: 'Fetch data from HTTP APIs' },
  { value: 'create.getProject', label: 'Get Project', description: 'Get project from Create API' },
  { value: 'create.listProjects', label: 'List Projects', description: 'List projects from Create API' },
];

interface AgentFormProps {
  initialData?: Partial<Agent>;
  onSubmit: (data: any) => Promise<void>;
  onCancel?: () => void;
  mode?: 'create' | 'edit';
}

export function AgentForm({
  initialData,
  onSubmit,
  onCancel,
  mode = 'create',
}: AgentFormProps) {
  const navigate = useNavigate();
  const [isMcpSelectorOpen, setIsMcpSelectorOpen] = useState(false);
  const [isPromptSelectorOpen, setIsPromptSelectorOpen] = useState(false);
  const [removingMcpId, setRemovingMcpId] = useState<string | null>(null);
  const [selectedPromptId, setSelectedPromptId] = useState<string>('');
  // Picking a catalog model is open to everyone; adding one to the shared
  // catalog is Monarch staff work (task 569).
  const canManageCatalog = useFlag('innovate.fe.model-configs.create');

  // Schema state (managed separately from useForm for SchemaBuilder compatibility)
  const [inputSchema, setInputSchema] = useState<Record<string, unknown> | null>(
    (initialData?.input as Record<string, unknown>) || null
  );
  const [outputSchema, setOutputSchema] = useState<Record<string, unknown> | null>(
    (initialData?.output as Record<string, unknown>) || null
  );
  const [schemaDirty, setSchemaDirty] = useState(false);

  // Fetch available Model configs
  const { data: modelConfigsData, isLoading: modelConfigsLoading } = useModelConfigs(1, 100);
  // The account's model policy (task 1147): disallowed configs grey out, the default is marked.
  const modelPolicy = useAccountModelPicks();
  const modelConfigs = modelConfigsData?.data || [];

  // Fetch available prompts for create mode dropdown
  const { data: promptsData, isLoading: promptsLoading } = usePrompts(1, 100);
  const prompts = promptsData?.data || [];

  // Fetch MCP connections for edit mode
  const agentId = initialData?.id;
  const { data: mcpConnections, isLoading: mcpLoading } = useAgentMcpConnections(
    mode === 'edit' ? agentId : undefined
  );
  const removeMcpConnection = useRemoveAgentMcpConnection();

  // Fetch active prompt for edit mode
  const { data: activePrompt, isLoading: activePromptLoading } = useActiveAgentPrompt(
    mode === 'edit' ? agentId : undefined
  );

  const handleRemoveMcp = async (mcpConnectionId: string) => {
    if (!agentId) return;
    if (removingMcpId === mcpConnectionId) {
      try {
        await removeMcpConnection.mutateAsync({ agentId, mcpConnectionId });
        setRemovingMcpId(null);
      } catch (error) {
        console.error('Failed to remove MCP connection:', error);
      }
    } else {
      setRemovingMcpId(mcpConnectionId);
    }
  };

  const {
    values,
    errors,
    isSubmitting,
    isDirty,
    isValid,
    handleChange,
    handleSubmit,
    setFieldValue,
  } = useForm<AgentFormData>({
    initialValues: {
      name: initialData?.name || '',
      description: initialData?.description || '',
      metadata: initialData?.metadata ? JSON.stringify(initialData.metadata, null, 2) : '',
      isActive: initialData?.isActive ?? true,
      modelConfigId: initialData?.modelConfigId || '',
      failoverModelConfigId: initialData?.failoverModelConfigId || '',
      tools: initialData?.tools || [],
    },
    requiredFields: ['name'],
    onSubmit: async (formValues) => {
      // Build submission data
      const data: any = {
        name: formValues.name,
        description: formValues.description || undefined,
        isActive: formValues.isActive,
        modelConfigId: formValues.modelConfigId || null,
        failoverModelConfigId: formValues.failoverModelConfigId || null,
        tools: formValues.tools,
      };

      // Include selected prompt ID for create mode
      if (mode === 'create' && selectedPromptId) {
        data.selectedPromptId = selectedPromptId;
      }

      // Include schemas from SchemaBuilder state
      if (inputSchema) {
        data.input = inputSchema;
      }
      if (outputSchema) {
        data.output = outputSchema;
      }

      // Parse metadata JSON
      if (formValues.metadata.trim()) {
        try {
          data.metadata = JSON.parse(formValues.metadata);
        } catch {
          throw new Error('Invalid JSON in Metadata field');
        }
      }

      await onSubmit(data);
    },
    validate: (values) => {
      const errors: Partial<Record<keyof AgentFormData, string>> = {};
      if (!values.name.trim()) {
        errors.name = 'Agent name is required';
      }
      // Validate metadata JSON
      if (values.metadata.trim()) {
        try {
          JSON.parse(values.metadata);
        } catch {
          errors.metadata = 'Invalid JSON format';
        }
      }
      return errors;
    },
  });

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/agents');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* 1. Name + Active (inline) */}
      {mode === 'create' && (
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <FormField
              label="Agent Name"
              name="name"
              type="text"
              value={values.name}
              onChange={handleChange}
              error={errors.name}
              required
              placeholder="Enter agent name"
            />
          </div>
          <div className="flex items-center pt-7">
            <input
              type="checkbox"
              id="isActive"
              name="isActive"
              checked={values.isActive}
              onChange={(e) => handleChange({ target: { name: 'isActive', value: e.target.checked } } as any)}
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
              Active
            </label>
          </div>
        </div>
      )}

      {/* 2. Description (full-width) */}
      <FormTextArea
        label="Description"
        name="description"
        value={values.description}
        onChange={handleChange}
        error={errors.description}
        placeholder="Enter agent description (Markdown supported)"
        rows={4}
      />

      {/* 3. Prompt Selection (prominent) */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-2" />
            <label className="block text-sm font-medium text-gray-900 dark:text-white">
              Active Prompt
            </label>
          </div>
          {mode === 'edit' && agentId && (
            <button
              type="button"
              onClick={() => setIsPromptSelectorOpen(true)}
              className="inline-flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
            >
              Change
            </button>
          )}
        </div>
        {mode === 'create' ? (
          <>
            <select
              value={selectedPromptId}
              onChange={(e) => setSelectedPromptId(e.target.value)}
              disabled={promptsLoading}
              className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
            >
              <option value="">No prompt (assign later)</option>
              {prompts.map((prompt) => (
                <option key={prompt.id} value={prompt.id}>
                  {prompt.name}
                </option>
              ))}
            </select>
            {selectedPromptId && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Selected prompt will be assigned after agent creation.
              </p>
            )}
          </>
        ) : activePromptLoading ? (
          <div className="animate-pulse">
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
          </div>
        ) : activePrompt ? (
          <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-900 rounded-md p-3">
            <span className="font-medium text-gray-900 dark:text-white">
              {activePrompt.name}
            </span>
            <a
              href={`/prompts/${activePrompt.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
              title="Open prompt in new tab"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </a>
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400 italic">
            No active prompt assigned.
          </p>
        )}
      </div>

      {/* 4. Input/Output Schemas (side-by-side) */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SchemaBuilder
          label="Input Schema"
          value={inputSchema}
          onChange={(schema) => {
            setInputSchema(schema);
            setSchemaDirty(true);
          }}
        />

        <SchemaBuilder
          label="Output Schema"
          value={outputSchema}
          onChange={(schema) => {
            setOutputSchema(schema);
            setSchemaDirty(true);
          }}
        />
      </div>

      {/* 5. Configuration Section (Model, Tools, MCP) */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-4">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white">Configuration</h3>

        {/* Model Config */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 min-w-0">
            <CpuChipIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className="text-sm text-gray-500 dark:text-gray-400">Model:</span>
          </div>
          <div className="flex-1 flex items-center gap-2">
            <select
              name="modelConfigId"
              value={values.modelConfigId}
              onChange={(e) => {
                handleChange(e);
                // The service refuses the same config as primary and failover.
                if (e.target.value && e.target.value === values.failoverModelConfigId) {
                  setFieldValue('failoverModelConfigId', '');
                }
              }}
              disabled={modelConfigsLoading}
              className="flex-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
            >
              <option value="">No default config</option>
              <ModelConfigPickerOptions
                configs={modelConfigs}
                selectedId={values.modelConfigId}
                policy={modelPolicy}
                label={(config) => `${config.name || config.model.modelId} (${config.model.provider?.key ?? 'unknown'})`}
              />
            </select>
            {canManageCatalog && (
              <Link
                to="/model-configs/new"
                className="inline-flex items-center px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                title="Create new Model config"
              >
                <PlusIcon className="h-4 w-4" />
              </Link>
            )}
          </div>
        </div>
        <ModelLifecycleNote configs={modelConfigs} selectedId={values.modelConfigId} />
        <ModelPolicyNote configs={modelConfigs} selectedId={values.modelConfigId} policy={modelPolicy} />

        {/* Failover Model Config (task 1301) */}
        <FailoverModelConfigPicker
          configs={modelConfigs}
          policy={modelPolicy}
          primaryId={values.modelConfigId}
          value={values.failoverModelConfigId}
          onChange={(id) => setFieldValue('failoverModelConfigId', id ?? '')}
          disabled={modelConfigsLoading}
        />

        {/* Tools */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <WrenchScrewdriverIcon className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-500 dark:text-gray-400">Tools:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {AVAILABLE_TOOLS.map((tool) => (
              <label
                key={tool.value}
                className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm cursor-pointer border transition-colors ${
                  values.tools.includes(tool.value)
                    ? 'bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-900 dark:text-indigo-200 dark:border-indigo-700'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
                }`}
              >
                <input
                  type="checkbox"
                  checked={values.tools.includes(tool.value)}
                  onChange={(e) => {
                    const newTools = e.target.checked
                      ? [...values.tools, tool.value]
                      : values.tools.filter((t) => t !== tool.value);
                    handleChange({ target: { name: 'tools', value: newTools } } as any);
                  }}
                  className="sr-only"
                />
                {tool.label}
              </label>
            ))}
          </div>
        </div>

        {/* MCP Connections */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <ServerStackIcon className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-500 dark:text-gray-400">MCP Connections:</span>
            </div>
            {mode === 'edit' && agentId && (
              <button
                type="button"
                onClick={() => setIsMcpSelectorOpen(true)}
                className="inline-flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add
              </button>
            )}
          </div>
          {mode === 'create' ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">
              MCP connections can be added after creating the agent.
            </p>
          ) : mcpLoading ? (
            <div className="animate-pulse">
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
            </div>
          ) : !mcpConnections || mcpConnections.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">
              No MCP connections linked.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {mcpConnections.map((amc) => (
                <span
                  key={amc.id}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded text-sm ${
                    amc.mcpConnection?.isActive
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}
                >
                  {amc.mcpConnection?.name || 'Unknown'}
                  {removingMcpId === amc.mcpConnectionId ? (
                    <>
                      <button
                        type="button"
                        onClick={() => handleRemoveMcp(amc.mcpConnectionId)}
                        disabled={removeMcpConnection.isPending}
                        className="text-red-600 dark:text-red-400 hover:text-red-800 disabled:opacity-50 ml-1"
                      >
                        {removeMcpConnection.isPending ? '...' : 'Yes'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setRemovingMcpId(null)}
                        className="text-gray-500 dark:text-gray-400"
                      >
                        No
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleRemoveMcp(amc.mcpConnectionId)}
                      className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      title="Remove"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 6. Metadata (collapsible) */}
      <Disclosure>
        {({ open }) => (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
            <Disclosure.Button className="flex w-full items-center justify-between p-4 text-left bg-gray-50 dark:bg-gray-800/50 rounded-t-lg">
              <span className="text-sm font-medium text-gray-900 dark:text-white">Metadata (JSON)</span>
              <ChevronRightIcon
                className={`h-5 w-5 text-gray-500 transition-transform ${open ? 'rotate-90' : ''}`}
              />
            </Disclosure.Button>
            <Disclosure.Panel className="p-4 bg-white dark:bg-gray-800">
              <textarea
                name="metadata"
                value={values.metadata}
                onChange={handleChange}
                placeholder='{"key": "value"}'
                rows={4}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm ${
                  errors.metadata ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {errors.metadata && (
                <p className="mt-1 text-sm text-red-600">{errors.metadata}</p>
              )}
            </Disclosure.Panel>
          </div>
        )}
      </Disclosure>

      <SmartFormActions
        mode={mode}
        isDirty={isDirty || schemaDirty}
        isValid={isValid}
        isSubmitting={isSubmitting}
        onCancel={handleCancel}
        submitLabel={mode === 'create' ? 'Create Agent' : 'Save Changes'}
      />

      {/* MCP Connection Selector Modal */}
      {mode === 'edit' && agentId && (
        <AgentMcpConnectionSelector
          agentId={agentId}
          isOpen={isMcpSelectorOpen}
          onClose={() => setIsMcpSelectorOpen(false)}
        />
      )}

      {/* Prompt Selector Modal */}
      {mode === 'edit' && agentId && (
        <AgentPromptSelector
          agentId={agentId}
          isOpen={isPromptSelectorOpen}
          onClose={() => setIsPromptSelectorOpen(false)}
        />
      )}
    </form>
  );
}
